/**
 * Agent activity filters component.
 *
 * Provides filters for agent decision history:
 * - Symbol text input
 * - Decision type select (Buy/Sell/Hold/Skip)
 * - Outcome select (Executed/Rejected/Failed/Queued/Awaiting Approval)
 * - Date range picker (start/end)
 * - Sort by select (Newest/Oldest/Highest Confidence/Lowest Confidence)
 * - Quick period buttons (1D/1W/1M/1Y/All)
 */
import { useState } from 'react';
import { Calendar, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { DecisionHistoryFilters } from '@/services/history';

interface AgentActivityFiltersProps {
  filters: DecisionHistoryFilters;
  onFiltersChange: (filters: DecisionHistoryFilters) => void;
}

type QuickPeriod = 'day' | 'week' | 'month' | 'year' | 'all';

const quickPeriods: { value: QuickPeriod; label: string }[] = [
  { value: 'day', label: '1D' },
  { value: 'week', label: '1W' },
  { value: 'month', label: '1M' },
  { value: 'year', label: '1Y' },
  { value: 'all', label: 'All' },
];

const decisionTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' },
  { value: 'hold', label: 'Hold' },
  { value: 'skip', label: 'Skip' },
];

const outcomeOptions = [
  { value: '', label: 'All Outcomes' },
  { value: 'executed', label: 'Executed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'failed', label: 'Failed' },
  { value: 'queued', label: 'Queued' },
  { value: 'awaiting_approval', label: 'Awaiting Approval' },
];

const sortOptions = [
  { value: 'created_at_desc', label: 'Newest' },
  { value: 'created_at_asc', label: 'Oldest' },
  { value: 'confidence_desc', label: 'Highest Confidence' },
  { value: 'confidence_asc', label: 'Lowest Confidence' },
];

const selectClassName =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

function periodToStartDate(period: QuickPeriod): string | undefined {
  if (period === 'all') return undefined;
  const now = new Date();
  const offsets: Record<string, () => void> = {
    day: () => now.setUTCDate(now.getUTCDate() - 1),
    week: () => now.setUTCDate(now.getUTCDate() - 7),
    month: () => now.setUTCMonth(now.getUTCMonth() - 1),
    year: () => now.setUTCFullYear(now.getUTCFullYear() - 1),
  };
  offsets[period]();
  return now.toISOString().split('T')[0];
}

function parseSortValue(value: string): { sortBy?: string; sortOrder?: string } {
  if (!value || value === 'created_at_desc') {
    return { sortBy: undefined, sortOrder: undefined };
  }
  const [sortBy, sortOrder] = value.split('_').reduce<[string, string]>(
    (acc, part, i, arr) => {
      if (i === arr.length - 1) {
        acc[1] = part;
      } else {
        acc[0] = acc[0] ? `${acc[0]}_${part}` : part;
      }
      return acc;
    },
    ['', '']
  );
  return { sortBy, sortOrder };
}

function currentSortValue(filters: DecisionHistoryFilters): string {
  if (!filters.sortBy) return 'created_at_desc';
  return `${filters.sortBy}_${filters.sortOrder || 'desc'}`;
}

export function AgentActivityFilters({
  filters,
  onFiltersChange,
}: AgentActivityFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);

  const handleSymbolChange = (value: string) => {
    onFiltersChange({
      ...filters,
      symbol: value || undefined,
      page: 1,
    });
  };

  const handleDecisionTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      decisionType: value || undefined,
      page: 1,
    });
  };

  const handleOutcomeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      outcome: value || undefined,
      page: 1,
    });
  };

  const handleStartDateChange = (value: string) => {
    onFiltersChange({
      ...filters,
      startDate: value || undefined,
      page: 1,
    });
  };

  const handleEndDateChange = (value: string) => {
    onFiltersChange({
      ...filters,
      endDate: value || undefined,
      page: 1,
    });
  };

  const handleSortChange = (value: string) => {
    const { sortBy, sortOrder } = parseSortValue(value);
    onFiltersChange({
      ...filters,
      sortBy,
      sortOrder,
      page: 1,
    });
  };

  const handlePeriodSelect = (period: QuickPeriod) => {
    onFiltersChange({
      ...filters,
      startDate: periodToStartDate(period),
      endDate: undefined,
      page: 1,
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      page: 1,
      pageSize: filters.pageSize,
    });
  };

  const hasActiveFilters =
    filters.symbol ||
    filters.decisionType ||
    filters.outcome ||
    filters.startDate ||
    filters.endDate ||
    filters.sortBy;

  return (
    <div className="space-y-4">
      {/* Quick period selection and filter toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex rounded-lg border p-1">
          {quickPeriods.map((period) => (
            <button
              key={period.value}
              onClick={() => handlePeriodSelect(period.value)}
              className={cn(
                'px-3 py-1 text-sm rounded-md transition-colors',
                'text-muted-foreground hover:text-foreground'
              )}
            >
              {period.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(hasActiveFilters && 'border-primary')}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                !
              </span>
            )}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              <X className="mr-1 h-4 w-4" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="rounded-lg border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Symbol filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Symbol</label>
              <Input
                placeholder="e.g. AAPL"
                value={filters.symbol || ''}
                onChange={(e) => handleSymbolChange(e.target.value.toUpperCase())}
              />
            </div>

            {/* Decision type filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Decision Type
              </label>
              <select
                className={selectClassName}
                value={filters.decisionType || ''}
                onChange={(e) => handleDecisionTypeChange(e.target.value)}
              >
                {decisionTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Outcome filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Outcome</label>
              <select
                className={selectClassName}
                value={filters.outcome || ''}
                onChange={(e) => handleOutcomeChange(e.target.value)}
              >
                {outcomeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Start date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Start Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  className="pl-10"
                  value={filters.startDate || ''}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
              </div>
            </div>

            {/* End date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">End Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  className="pl-10"
                  value={filters.endDate || ''}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                />
              </div>
            </div>

            {/* Sort by */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Sort By</label>
              <select
                className={selectClassName}
                value={currentSortValue(filters)}
                onChange={(e) => handleSortChange(e.target.value)}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentActivityFilters;
